import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Layers, Pencil, ExternalLink, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../AuthProvider';
import { supabase } from '../../../lib/supabase';
import type { Parcours, ParcoursStep, Block } from '../../../types/parcours';
import LandingHero from '../../../components/parcours/LandingHero';
import BlocksRenderer from '../../../components/parcours/BlocksRenderer';
import ParcoursStatusBadge from '../../../components/admin/ParcoursStatusBadge';

export default function ParcoursPreviewAdmin() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [parcours, setParcours] = useState<Parcours | null>(null);
  const [steps, setSteps] = useState<ParcoursStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function init() {
      if (!user) { navigate('/'); return; }
      const { data: ud } = await supabase.from('users').select('role').eq('id', user.id).maybeSingle();
      if (ud?.role !== 'admin' && ud?.role !== 'editor') { navigate('/'); return; }
      await load();
    }
    init();
  }, [user?.id, id]);

  async function load() {
    if (!id) return;
    try {
      setLoading(true);

      const { data: p, error: pe } = await supabase
        .from('parcours')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (pe) throw pe;
      if (!p) { setNotFound(true); setLoading(false); return; }
      setParcours(p as Parcours);

      const { data: s, error: se } = await supabase
        .from('parcours_steps')
        .select('*')
        .eq('parcours_id', id)
        .order('order_index', { ascending: true });

      if (se) throw se;
      setSteps(
        (s ?? []).map((row) => ({
          ...row,
          blocks: Array.isArray(row.blocks) ? row.blocks : [],
        })) as ParcoursStep[]
      );
    } catch {
      toast.error('Impossible de charger le parcours.');
    } finally {
      setLoading(false);
    }
  }

  // ── États ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-600 border-t-transparent" />
      </div>
    );
  }

  if (notFound || !parcours) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div className="mx-auto max-w-4xl px-6 py-24 text-center">
          <p className="text-5xl font-bold text-slate-200 mb-4">404</p>
          <h1 className="text-2xl font-semibold text-slate-800 mb-2">Parcours introuvable</h1>
          <p className="text-slate-500 mb-8">Ce parcours n'existe pas ou a été supprimé.</p>
          <Link to="/admin/parcours" className="inline-flex items-center gap-2 text-sm font-medium text-pink-600 hover:text-pink-700">
            <ChevronLeft className="h-4 w-4" /> Retour à la liste
          </Link>
        </div>
      </div>
    );
  }

  const publishedSteps = steps.filter((s) => s.status === 'published');
  const draftSteps = steps.filter((s) => s.status === 'draft');

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Prévisualisation parcours | Recruteurs.io</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* ── Bannière admin sticky ─────────────────────────────────────────── */}
      <div className="sticky top-0 z-50 border-b border-amber-200 bg-amber-50">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
            <div className="min-w-0">
              <span className="text-sm font-semibold text-amber-800">Prévisualisation admin</span>
              <span className="ml-2 hidden text-xs text-amber-600 sm:inline">
                Ce parcours peut ne pas être visible publiquement.
              </span>
            </div>
            <ParcoursStatusBadge status={parcours.status} />
            <span className="hidden text-xs text-amber-600 sm:inline">
              · {steps.length} étape{steps.length !== 1 ? 's' : ''}&nbsp;
              ({publishedSteps.length} publiée{publishedSteps.length !== 1 ? 's' : ''},&nbsp;
              {draftSteps.length} brouillon{draftSteps.length !== 1 ? 's' : ''})
            </span>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Link
              to={`/admin/parcours/${id}/edit`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 transition-colors"
            >
              <Pencil className="h-3.5 w-3.5" /> Modifier
            </Link>
            <Link
              to={`/admin/parcours/${id}/steps`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 transition-colors"
            >
              <Layers className="h-3.5 w-3.5" /> Étapes
            </Link>
            {parcours.status === 'published' && (
              <a
                href={`/decouvrir/${parcours.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Page publique
              </a>
            )}
            <Link
              to="/admin/parcours"
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Liste
            </Link>
          </div>
        </div>
      </div>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <LandingHero
        badge={parcours.sector ?? undefined}
        title={parcours.company_name}
        subtitle={parcours.tagline ?? undefined}
        heroImageUrl={parcours.company_cover ?? undefined}
        companyLogoUrl={parcours.company_logo ?? undefined}
      />

      {/* ── Description ──────────────────────────────────────────────────── */}
      {parcours.description && (
        <section className="border-b border-neutral-100 bg-white py-10">
          <div className="mx-auto max-w-3xl px-6">
            <p className="text-neutral-700 leading-relaxed whitespace-pre-line">{parcours.description}</p>
            <div className="mt-6 flex flex-wrap gap-4">
              {parcours.location && (
                <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-600">
                  {parcours.location}
                </span>
              )}
              {parcours.employee_count && (
                <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-600">
                  {parcours.employee_count}
                </span>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Sommaire des étapes ───────────────────────────────────────────── */}
      <section className="border-b border-neutral-100 bg-neutral-50 py-10">
        <div className="mx-auto max-w-3xl px-6">
          <h2 className="mb-6 text-xl font-bold text-neutral-900">
            Sommaire — {steps.length} étape{steps.length !== 1 ? 's' : ''}
          </h2>
          {steps.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center">
              <p className="text-sm text-slate-500 mb-3">Aucune étape créée pour ce parcours.</p>
              <Link
                to={`/admin/parcours/${id}/steps`}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-pink-600 hover:text-pink-700"
              >
                <Layers className="h-4 w-4" /> Créer une étape
              </Link>
            </div>
          ) : (
            <ol className="space-y-2">
              {steps.map((step, idx) => (
                <li key={step.id}>
                  <div className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white px-4 py-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-xs font-bold text-neutral-500">
                      {idx + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-neutral-900">{step.title}</p>
                      <p className="text-xs text-neutral-400 font-mono">{step.step_id}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <ParcoursStatusBadge status={step.status} />
                      <span className="text-xs text-slate-400">{step.blocks.length} bloc{step.blocks.length !== 1 ? 's' : ''}</span>
                      <Link
                        to={`/admin/parcours/${id}/steps/${step.id}/edit`}
                        className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                        title="Modifier l'étape"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      {/* ── Aperçu de chaque étape ────────────────────────────────────────── */}
      {steps.length > 0 && (
        <section className="py-12">
          <div className="mx-auto max-w-3xl px-6">
            <h2 className="mb-8 text-xl font-bold text-neutral-900">Aperçu des étapes</h2>
            <div className="space-y-10">
              {steps.map((step, idx) => (
                <StepPreviewCard
                  key={step.id}
                  step={step}
                  index={idx + 1}
                  editUrl={`/admin/parcours/${id}/steps/${step.id}/edit`}
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// ── Carte d'aperçu d'une étape ─────────────────────────────────────────────────

interface StepPreviewCardProps {
  step: ParcoursStep;
  index: number;
  editUrl: string;
}

function StepPreviewCard({ step, index, editUrl }: StepPreviewCardProps) {
  const isDraft = step.status === 'draft';

  return (
    <div className={`rounded-2xl border ${isDraft ? 'border-dashed border-slate-300 bg-slate-50/50' : 'border-neutral-200 bg-white'} overflow-hidden shadow-sm`}>
      {/* En-tête étape */}
      <div className={`flex items-start justify-between gap-4 px-6 py-4 ${isDraft ? 'border-b border-dashed border-slate-200' : 'border-b border-neutral-100'}`}>
        <div className="flex items-center gap-3 min-w-0">
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${isDraft ? 'bg-slate-200 text-slate-500' : 'bg-pink-100 text-pink-700'}`}>
            {index}
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-neutral-900 truncate">{step.title}</h3>
            {step.description && (
              <p className="mt-0.5 text-sm text-neutral-500 line-clamp-1">{step.description}</p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ParcoursStatusBadge status={step.status} />
          <Link
            to={editUrl}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <Pencil className="h-3 w-3" /> Modifier
          </Link>
        </div>
      </div>

      {/* Contenu blocs */}
      <div className={`px-6 py-6 ${isDraft ? 'opacity-75' : ''}`}>
        {step.blocks.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Aucun bloc dans cette étape.</p>
        ) : (
          <BlocksRenderer blocks={step.blocks as Block[]} />
        )}
      </div>
    </div>
  );
}
